import { createPortal } from "react-dom";
import { Minus, Plus, Trash2, MessageCircle, X } from "lucide-react";

const WHATSAPP = "917624853840";

const buildMessage = (items, totals) => {
  const lines = ["*New order enquiry — muthaparas.com*", ""];
  const byQuality = items.reduce((acc, i) => {
    (acc[i.quality_name] = acc[i.quality_name] || []).push(i);
    return acc;
  }, {});

  Object.entries(byQuality).forEach(([quality, rows]) => {
    lines.push(`*${quality}*`);
    rows.forEach((i) => {
      if (i.cut_length) {
        lines.push(`  Cut piece ${i.cut_length} — ${i.thans} pack(s) of ${i.pieces_per_pack} colours`);
      } else {
        lines.push(`  #${i.number} — ${i.thans} than × ${i.than_meters}m = ${i.thans * i.than_meters}m`);
      }
    });
    lines.push("");
  });

  if (totals.meters) lines.push(`Total fabric: ${totals.thans} ${totals.thans === 1 ? "than" : "thans"} · ${totals.meters} metres`);
  if (totals.packs) lines.push(`Cut piece packs: ${totals.packs} (${totals.pieces} pieces)`);
  lines.push("");
  lines.push("Please confirm availability, rates and parcel count. (5% GST extra)");
  return lines.join("\n");
};

export const OrderTray = ({ items, totals, onSetThans, onRemove, onClear, open, onClose, onSubmit, reference }) => {
  const href = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(buildMessage(items, totals))}`;

  // Portaled to <body>: inside the page wrapper, animations and backdrop filters on ancestors
  // turn them into the containing block for position:fixed, which left the tray stranded
  // thousands of pixels down the document on small screens.
  return createPortal(
    <aside
      data-testid="order-tray"
      className={`fixed z-40 inset-x-0 bottom-0 lg:inset-x-auto lg:right-6 xl:right-10 lg:bottom-auto lg:top-28 lg:w-[360px] border-t lg:border lg:rounded-sm flex-col ${
        open ? "flex" : "hidden lg:flex"
      }`}
      style={{
        backgroundColor: "var(--mp-card)",
        borderColor: "var(--mp-line)",
        maxHeight: "min(80vh, 640px)",
        boxShadow: "0 -12px 40px rgba(16,16,16,0.18)",
      }}
    >
      <div
        className="flex items-center justify-between px-5 py-3.5 border-b"
        style={{ borderColor: "var(--mp-line)" }}
      >
        <div>
          <p
            className="text-[10px] tracking-[0.18em] uppercase"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
          >
            Your order
          </p>
          <p
            className="text-base"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--mp-text)" }}
            data-testid="tray-line-count"
          >
            {totals.shades} {totals.shades === 1 ? "line" : "lines"}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {items.length > 0 && (
            <button
              onClick={onClear}
              data-testid="tray-clear"
              aria-label="Clear order"
              className="p-2 rounded-sm transition-opacity hover:opacity-60"
              style={{ color: "var(--mp-muted)" }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="tray-close"
            aria-label="Close order tray"
            className="lg:hidden p-2 rounded-sm"
            style={{ color: "var(--mp-muted)" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="tray-items">
        {items.length === 0 && (
          <p
            className="py-10 text-center text-sm"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="tray-empty"
          >
            Tap any shade to start your order.
          </p>
        )}
        {items.map((i) => (
          <div
            key={i.key}
            className="flex items-center gap-3 px-2 py-2 rounded-sm"
            style={{ backgroundColor: "var(--mp-surface)" }}
            data-testid={`tray-item-${i.cut_length ? `cut-${i.cut_length.replace(/\s+/g, "-")}` : i.number}`}
          >
            <div
              className="w-10 h-10 rounded-sm shrink-0 overflow-hidden border"
              style={{ backgroundColor: i.hex || "var(--mp-line)", borderColor: "var(--mp-line)" }}
            >
              {i.image_url && <img src={i.image_url} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-xs font-semibold truncate"
                style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
              >
                {i.cut_length ? `Cut piece ${i.cut_length}` : `#${i.number}`}
              </p>
              <p className="text-[11px] truncate" style={{ color: "var(--mp-muted)" }}>
                {i.quality_name} ·{" "}
                {i.cut_length
                  ? `${i.pieces_per_pack} colours/pack`
                  : `${i.thans * i.than_meters}m`}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={() => onSetThans(i.key, i.thans - 1)}
                data-testid={`tray-dec-${i.cut_length ? `cut-${i.cut_length.replace(/\s+/g, "-")}` : i.number}`}
                aria-label="Reduce quantity"
                className="p-1.5 rounded-sm border transition-colors"
                style={{ borderColor: "var(--mp-line)", color: "var(--mp-text)" }}
              >
                <Minus className="w-3 h-3" />
              </button>
              <span
                className="w-7 text-center text-sm font-semibold tabular-nums"
                style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}
                data-testid={`tray-qty-${i.cut_length ? `cut-${i.cut_length.replace(/\s+/g, "-")}` : i.number}`}
              >
                {i.thans}
              </span>
              <button
                onClick={() => onSetThans(i.key, i.thans + 1)}
                data-testid={`tray-inc-${i.cut_length ? `cut-${i.cut_length.replace(/\s+/g, "-")}` : i.number}`}
                aria-label="Increase quantity"
                className="p-1.5 rounded-sm border transition-colors"
                style={{ borderColor: "var(--mp-line)", color: "var(--mp-text)" }}
              >
                <Plus className="w-3 h-3" />
              </button>
              <button
                onClick={() => onRemove(i.key)}
                data-testid={`tray-remove-${i.cut_length ? `cut-${i.cut_length.replace(/\s+/g, "-")}` : i.number}`}
                aria-label="Remove line"
                className="p-1.5 rounded-sm transition-opacity hover:opacity-60"
                style={{ color: "var(--mp-muted)" }}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="px-5 py-4 border-t space-y-1.5" style={{ borderColor: "var(--mp-line)" }}>
        {totals.meters > 0 && (
          <Row label={`${totals.thans} ${totals.thans === 1 ? "than" : "thans"}`} value={`${totals.meters} m`} testid="tray-total-meters" />
        )}
        {totals.packs > 0 && (
          <Row label={`${totals.packs} cut-piece packs`} value={`${totals.pieces} pieces`} testid="tray-total-packs" />
        )}
        <div
          className="pt-2 mt-1 border-t text-[11px] space-y-0.5"
          style={{ borderColor: "var(--mp-line)", color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          data-testid="tray-transport-rates"
        >
          <p>Transport / local lorry — Rs. 100 per parcel</p>
          <p>Bus / travels — Rs. 150 per parcel</p>
          <p className="pt-0.5">Parcel count confirmed by us · Rates on request · 5% GST extra</p>
        </div>
        {reference && (
          <p
            className="text-[11px] pt-1"
            style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
            data-testid="tray-reference"
          >
            Sent · reference {reference}
          </p>
        )}
        <button
          type="button"
          onClick={() => items.length && onSubmit(href)}
          data-testid="tray-whatsapp-submit"
          aria-disabled={items.length === 0}
          className="flex items-center justify-center gap-2 w-full text-sm font-medium py-3.5 mt-2 rounded-sm transition-opacity hover:opacity-90"
          style={{
            backgroundColor: items.length ? "var(--mp-copper-solid)" : "var(--mp-line)",
            color: items.length ? "#FFFFFF" : "var(--mp-muted)",
            fontFamily: "'Manrope', sans-serif",
            pointerEvents: items.length ? "auto" : "none",
          }}
        >
          <MessageCircle className="w-4 h-4" />
          Send order on WhatsApp
        </button>
      </div>
    </aside>,
    document.body
  );
};

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between text-xs" data-testid={testid}>
    <span style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}>{label}</span>
    <span className="font-semibold tabular-nums" style={{ color: "var(--mp-text)", fontFamily: "'Manrope', sans-serif" }}>
      {value}
    </span>
  </div>
);
